import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Tag } from 'lucide-react';
import { motion } from 'framer-motion';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Articles = ({ limit = 3 }) => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        // First try to seed articles if none exist
        await axios.post(`${API}/seed-articles`);
        
        const response = await axios.get(`${API}/articles`);
        setArticles(response.data.slice(0, limit));
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, [limit]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <section
      id="articles"
      data-testid="articles-section"
      className="section-padding bg-[#F5F2EB]"
    >
      <div className="section-container">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16"
        >
          <div className="max-w-2xl">
            <span className="caption mb-4 block">Ayurveda Knowledge</span>
            <h2
              className="heading-2 text-[#2D4A3E] mb-4"
              data-testid="articles-heading"
            >
              Health & Wellness Articles
            </h2>
            <p className="body-large text-[#6B6B6B]">
              Explore our collection of articles on Ayurvedic wisdom, healthy
              living, and natural remedies.
            </p>
          </div>

          <Link
            to="/articles"
            className="flex items-center gap-2 text-[#C67D63] hover:text-[#a86850] font-medium group"
            data-testid="view-all-articles-link"
          >
            View All Articles
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Link>
        </motion.div>

        {/* Articles Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="bg-gray-200 rounded-2xl h-48 mb-4" />
                <div className="h-4 bg-gray-200 rounded w-24 mb-2" />
                <div className="h-6 bg-gray-200 rounded w-full mb-2" />
                <div className="h-4 bg-gray-200 rounded w-full" />
              </div>
            ))}
          </div>
        ) : articles.length === 0 ? (
          <div className="text-center py-12 text-[#6B6B6B]">
            <p>No articles available yet. Check back soon!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles.map((article, index) => (
              <motion.article
                key={article.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="article-card group"
                data-testid={`article-card-${article.slug}`}
              >
                <Link to={`/articles/${article.slug}`}>
                  {/* Image */}
                  <div className="relative rounded-2xl overflow-hidden mb-4">
                    <img
                      src={article.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800'}
                      alt={article.title}
                      className="article-image w-full h-48"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </div>

                  {/* Meta */}
                  <div className="flex items-center gap-4 mb-3 text-sm text-[#6B6B6B]">
                    <span className="flex items-center gap-1">
                      <Tag className="w-3.5 h-3.5" />
                      {article.category}
                    </span>
                    <span className="flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(article.created_at)}
                    </span>
                  </div>

                  {/* Title & Excerpt */}
                  <h3 className="heading-3 text-[#2D4A3E] mb-2 group-hover:text-[#C67D63] transition-colors duration-200">
                    {article.title}
                  </h3>
                  <p className="text-[#6B6B6B] leading-relaxed line-clamp-2">
                    {article.excerpt}
                  </p>

                  {/* Read More */}
                  <span className="inline-flex items-center gap-2 mt-4 text-[#C67D63] font-medium group/link">
                    Read More
                    <ArrowRight className="w-4 h-4 transition-transform group-hover/link:translate-x-1" />
                  </span>
                </Link>
              </motion.article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Articles;
